import { CircularProgress } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getYTicks } from "./utils";

type DataYKey = `y${number}`;

type YData = Record<DataYKey, number>;

const strokeDashes = ["1 0", "3 3", "6 6", "12 4 4", "12 2 2 2"];
const defaultDashes = "6 3";
const chartMargin = 50;

interface Props {
  chartId: string;
  data: Array<{ x: string | number } & YData>;
  curveLabels?: Record<DataYKey, string>;
  axisAllocation: { left: Array<keyof YData>; right?: Array<keyof YData> };
  width?: number;
  height?: number;
  xlabel?: string;
  xTickLabelsAngled?: true;
  xTickFormatter?: (value: number | string) => string;
  tooltipXValueFormatter?: (value: number | string) => string;
  xAxisLabelOffset?: number;
  ylabelAxisLeft?: string;
  ylabelAxisRight?: string;
  yValueFormatterAxisLeft?: (tick: number | string) => string;
  yValueFormatterAxisRight?: (tick: number | string) => string;
  noTicksAxisLeft?: number;
  noTicksAxisRight?: number;
  showCurveLegend?: true;
  averageLines?: Array<keyof YData>;
  averageLineLabelsWithSpecifier?: boolean;
  lineColors?: Record<DataYKey, string>;
  lineAndAxisOpacity?: number;
  // minLines?: Array<keyof YData>;
  // maxLines?: Array<keyof YData>;
}

const getHexOpacity = (lineAndAxisOpacity: number | undefined) => {
  const opacity =
    lineAndAxisOpacity !== undefined
      ? Math.round(lineAndAxisOpacity * 255).toString(16)
      : "FF";
  return opacity.length === 1 ? `0${opacity}` : opacity;
};

const LineChartRecharts: React.FC<Props> = ({
  chartId,
  data,
  curveLabels,
  axisAllocation,
  width,
  height,
  xlabel,
  xTickLabelsAngled,
  xTickFormatter,
  tooltipXValueFormatter,
  xAxisLabelOffset,
  ylabelAxisLeft,
  ylabelAxisRight,
  yValueFormatterAxisLeft,
  yValueFormatterAxisRight,
  noTicksAxisLeft,
  noTicksAxisRight,
  showCurveLegend,
  averageLines,
  averageLineLabelsWithSpecifier,
  lineColors,
  lineAndAxisOpacity,
}: // minLines,
// maxLines,
Props) => {
  const opacity = getHexOpacity(lineAndAxisOpacity);
  const xAxisColor = `#666666${opacity}`;
  const leftAxisColor = `#666666${opacity}`;
  const leftAxisColorTwoAxes = `#3c78aa${opacity}`;
  const rightAxisColor = `#ea6a47${opacity}`;

  const leftLineColors = [
    `#3c78aa${opacity}`,
    `#8b6a95${opacity}`,
    `#5baa9f${opacity}`,
  ];
  const leftLineColorTwoAxes = `#3c78aa${opacity}`;
  const rightLineColor = `#ea6a47${opacity}`;

  const [yAxisLeftValues, setYAxisLeftValues] = useState<number[]>([]);
  const [yAxisRightValues, setYAxisRightValues] = useState<number[]>([]);

  const gatherYValuesForAxis = useCallback(
    (axisDataKeys: Array<keyof YData>) => {
      return data.reduce<number[]>((allValues, datapoint) => {
        const datapointValues = axisDataKeys.map(
          (datakey) => datapoint[datakey]
        );
        return allValues.concat(datapointValues);
      }, []);
    },
    [data]
  );

  useEffect(() => {
    if (data && data.length) {
      setYAxisLeftValues(gatherYValuesForAxis(axisAllocation.left));
      if (axisAllocation.right) {
        setYAxisRightValues(gatherYValuesForAxis(axisAllocation.right));
      }
    }
  }, [axisAllocation, data, gatherYValuesForAxis]);

  const getCurveLabel = (dataKey: DataYKey) =>
    curveLabels && curveLabels[dataKey] ? curveLabels[dataKey] : dataKey;

  const getAverage = (dataKey: DataYKey) =>
    data.reduce((sum, val) => sum + val[dataKey], 0) / data.length;

  const getRightAxisColor = () => rightAxisColor + "b0";

  const getRightAxisLineColor = (
    key: DataYKey,
    transparency?: "transparent"
  ) => {
    let color: string;
    if (lineColors && Object.keys(lineColors).includes(key)) {
      color = `${lineColors[key]}${opacity}`;
    } else {
      color = rightLineColor;
    }
    if (transparency) {
      return color.slice(0, -2) + "a0";
    } else {
      return color;
    }
  };

  const getLeftAxisLineColor = (
    key: DataYKey,
    lineIndex: number,
    transparency?: "transparent"
  ) => {
    let color: string;
    if (lineColors && Object.keys(lineColors).includes(key)) {
      color = `${lineColors[key]}${opacity}`;
    } else {
      color = yAxisRightValues.length
        ? leftLineColorTwoAxes
        : leftLineColors[lineIndex % leftLineColors.length];
    }
    if (transparency) {
      return color.slice(0, -2) + "a0";
    } else {
      return color;
    }
  };

  const getLeftAxisColor = () =>
    yAxisRightValues.length ? leftAxisColorTwoAxes : leftAxisColor;

  const getLeftAxisLineDashStyle = (lineIndex: number, dashed?: "dashed") =>
    yAxisRightValues.length
      ? strokeDashes[lineIndex % strokeDashes.length]
      : dashed
      ? defaultDashes
      : strokeDashes[0];

  const renderAverageLineLabelText = ({
    x,
    y,
    dataKey,
    lineIndex,
    axis,
  }: {
    x: number;
    y: number;
    dataKey: DataYKey;
    lineIndex: number;
    axis: "left" | "right";
  }): React.ReactElement => {
    const xPos = x;
    const yPos = y;

    return (
      <text
        id={`ref_line_text_${dataKey}_${chartId}`}
        x={xPos}
        y={yPos}
        fill={
          axis === "right"
            ? getRightAxisLineColor(dataKey)
            : getLeftAxisLineColor(dataKey, lineIndex)
        }
      >
        {averageLineLabelsWithSpecifier
          ? `Durchschnitt: ${getCurveLabel(dataKey)}`
          : "Durchschnitt"}
      </text>
    );
  };

  const renderAverageLineLabelBackground = ({
    x,
    y,
    width,
  }: {
    x: number;
    y: number;
    width: number;
  }): React.ReactElement => {
    const xPos = x;
    const yPos = y;
    // if (axis === "right" && propertiesLine && textLength) {
    //   xPos = propertiesLine.chartWidth - textLength - 115;
    // }

    return (
      <rect
        width={width}
        height={20}
        x={xPos - 5}
        y={yPos - 21}
        fill="#ffffffac"
      />
    );
  };

  return (
    <ResponsiveContainer width={width ?? "100%"} height={height ?? 400}>
      {data.length ? (
        <LineChart
          id={chartId}
          data={data}
          margin={{
            bottom: chartMargin,
            top: chartMargin,
            left: chartMargin,
            right: chartMargin,
          }}
          style={{ fontSize: "1rem" }}
        >
          {showCurveLegend && (
            <Legend
              verticalAlign="top"
              iconType="plainline"
              iconSize={24}
              height={36}
              wrapperStyle={{
                top: 15,
              }}
              formatter={(value) => getCurveLabel(value)}
            />
          )}
          <Tooltip
            labelFormatter={(xValue) =>
              tooltipXValueFormatter ? tooltipXValueFormatter(xValue) : xValue
            }
            formatter={(value: number, name: DataYKey) => {
              let formattedValue: string | number;
              if (
                axisAllocation.left.includes(name) &&
                yValueFormatterAxisLeft
              ) {
                formattedValue = yValueFormatterAxisLeft(value);
              } else if (
                axisAllocation.right?.includes(name) &&
                yValueFormatterAxisRight
              ) {
                formattedValue = yValueFormatterAxisRight(value);
              } else {
                formattedValue = value;
              }
              return [formattedValue, getCurveLabel(name)];
            }}
          />
          <CartesianGrid stroke="#ccc" />
          <XAxis
            dataKey="x"
            angle={xTickLabelsAngled ? -45 : 0}
            textAnchor={xTickLabelsAngled ? "end" : "middle"}
            interval={0}
            tick={{
              fill: xAxisColor,
            }}
            tickFormatter={xTickFormatter}
          >
            {xlabel && (
              <Label
                fill={xAxisColor}
                value={xlabel}
                offset={xAxisLabelOffset ?? 10}
                position="bottom"
              />
            )}
          </XAxis>
          <YAxis
            domain={["dataMin", "dataMax"]}
            ticks={getYTicks(yAxisLeftValues, noTicksAxisLeft)}
            tick={{
              fill: getLeftAxisColor(),
            }}
            tickFormatter={yValueFormatterAxisLeft}
            yAxisId="left"
          >
            {ylabelAxisLeft && (
              <Label
                value={ylabelAxisLeft}
                offset={5}
                fill={getLeftAxisColor()}
                position="left"
                angle={-90}
                style={{ textAnchor: "middle" }}
              />
            )}
          </YAxis>

          {axisAllocation.left.map((key, index) => (
            // eslint-disable-next-line react/jsx-key
            <Line
              yAxisId="left"
              type="linear"
              isAnimationActive={false}
              dataKey={key}
              stroke={getLeftAxisLineColor(key, index)}
              strokeWidth={3}
              strokeDasharray={getLeftAxisLineDashStyle(index)}
            />
          ))}

          {yAxisRightValues.length && (
            <>
              <YAxis
                domain={["dataMin", "dataMax"]}
                ticks={getYTicks(yAxisRightValues, noTicksAxisRight)}
                tick={{ fill: getRightAxisColor() }}
                yAxisId="right"
                orientation="right"
                tickFormatter={yValueFormatterAxisRight}
              >
                {ylabelAxisRight && (
                  <Label
                    value={ylabelAxisRight}
                    fill={getRightAxisColor()}
                    offset={5}
                    position="right"
                    angle={-90}
                    style={{ textAnchor: "middle" }}
                  />
                )}
              </YAxis>

              {axisAllocation.right?.map((key, index) => {
                return (
                  // eslint-disable-next-line react/jsx-key
                  <Line
                    yAxisId="right"
                    type="linear"
                    dataKey={key}
                    isAnimationActive={false}
                    stroke={getRightAxisLineColor(key)}
                    strokeWidth={3}
                    strokeDasharray={strokeDashes[index % strokeDashes.length]}
                  />
                );
              })}
            </>
          )}

          {axisAllocation.left.map(
            (key, index) =>
              averageLines?.includes(key) && (
                <ReferenceLine
                  yAxisId="left"
                  y={getAverage(key)}
                  stroke={getLeftAxisLineColor(key, index)}
                  strokeWidth={2.5}
                  strokeDasharray={getLeftAxisLineDashStyle(index, "dashed")}
                >
                  <Label
                    content={({ viewBox }: { viewBox: any }) => {
                      const textElement = document.querySelector<SVGTextElement>(
                        `[id='ref_line_text_${key}_${chartId}']`
                      );
                      return viewBox && textElement ? (
                        renderAverageLineLabelBackground({
                          x: viewBox.x + 10,
                          y: viewBox.y - 1,
                          width: textElement.getComputedTextLength() + 10,
                        })
                      ) : (
                        <g />
                      );
                    }}
                  />
                  <Label
                    content={({ viewBox }: { viewBox: any }) =>
                      viewBox ? (
                        renderAverageLineLabelText({
                          x: viewBox.x + 10,
                          y: viewBox.y - 5,
                          dataKey: key,
                          lineIndex: index,
                          axis: "left",
                        })
                      ) : (
                        <g />
                      )
                    }
                  />
                </ReferenceLine>
              )
          )}
          {yAxisRightValues.length &&
            axisAllocation.right?.map(
              (key, index) =>
                averageLines?.includes(key) && (
                  <ReferenceLine
                    yAxisId="right"
                    y={getAverage(key)}
                    stroke={getRightAxisLineColor(key)}
                    strokeWidth={2}
                    strokeDasharray={strokeDashes[index % strokeDashes.length]}
                  >
                    <Label
                      content={({ viewBox }: { viewBox: any }) => {
                        const textElement = document.querySelector<SVGTextElement>(
                          `[id='ref_line_text_${key}_${chartId}']`
                        );
                        return viewBox && textElement ? (
                          renderAverageLineLabelBackground({
                            x: viewBox.x + 10,
                            y: viewBox.y - 1,
                            width: textElement.getComputedTextLength() + 10,
                          })
                        ) : (
                          <g />
                        );
                      }}
                    />
                    <Label
                      content={({ viewBox }: { viewBox: any }) =>
                        viewBox ? (
                          renderAverageLineLabelText({
                            x: viewBox.x + 10,
                            y: viewBox.y - 5,
                            dataKey: key,
                            lineIndex: index,
                            axis: "right",
                          })
                        ) : (
                          <g />
                        )
                      }
                    />
                  </ReferenceLine>
                )
            )}
        </LineChart>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </div>
      )}
    </ResponsiveContainer>
  );
};

export default LineChartRecharts;



// WEBPACK FOOTER //
// ./src/components/generic/LineChart.tsx